import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { ChevronLeft, Coins, LogOut, Receipt } from 'lucide-react';
import { useAppStore } from '@/store';
import { getPrimaryNavItems, PROFILE_SECONDARY_LINKS, getActivePrimaryNav } from '@/config/navigation';
import { NotificationBell } from '@/components/NotificationBell';

interface DesktopLayoutProps {
  children: React.ReactNode;
}

export const DesktopLayout: React.FC<DesktopLayoutProps> = ({ children }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { fastLogout, userBalance, fetchUserBalance, balanceLoading, user } = useAppStore();
  const [collapsed, setCollapsed] = React.useState(() => localStorage.getItem('mipt-sidebar-collapsed') === 'true');
  const [navKey, setNavKey] = React.useState(0); // Force re-render of navigation

  // Check if user has low tokens (<= 100)
  const lowTokens = userBalance ? userBalance.available_tokens <= 100 : false;
  const primaryNavItems = getPrimaryNavItems(lowTokens);
  const activePrimary = getActivePrimaryNav(location.pathname, primaryNavItems);

  // Staff-only admin links live in the "More" section without their own title.
  const secondaryLinks = React.useMemo(
    () =>
      user?.is_staff
        ? [...PROFILE_SECONDARY_LINKS, { label: 'Transactions', href: '/admin/transactions', icon: Receipt }]
        : PROFILE_SECONDARY_LINKS,
    [user?.is_staff],
  );

  // Force navigation re-render when userBalance changes
  React.useEffect(() => {
    if (userBalance) {
      setNavKey(prev => prev + 1);
    }
  }, [userBalance]);

  // Debug logging
  console.log('DesktopLayout - userBalance:', userBalance);
  console.log('DesktopLayout - available_tokens:', userBalance?.available_tokens);
  console.log('DesktopLayout - lowTokens:', lowTokens);
  console.log('DesktopLayout - primaryNavItems:', primaryNavItems);
  console.log('DesktopLayout - navKey:', navKey);

  React.useEffect(() => {
    localStorage.setItem('mipt-sidebar-collapsed', String(collapsed));
  }, [collapsed]);

  React.useEffect(() => {
    if (!userBalance) {
      fetchUserBalance().catch(console.error);
    }
  }, [userBalance, fetchUserBalance]);

  const handleLogout = () => {
    fastLogout();
  };

  return (
    <div className="hidden h-screen overflow-hidden bg-[#f7f5ef] lg:flex">
      <aside className={`flex h-screen flex-col border-r border-white/70 bg-[#fffaf2]/92 backdrop-blur-xl transition-all duration-200 ${collapsed ? 'w-24' : 'w-72'}`}>
        <div className="shrink-0 border-b border-gray-200 px-4 py-5">
          <div className={`flex items-center ${collapsed ? 'justify-center' : 'justify-between'} gap-3`}>
            <button
              onClick={() => navigate('/dashboard')}
              className={`flex items-center gap-3 rounded-2xl transition-colors hover:opacity-90 ${collapsed ? 'justify-center' : ''}`}
              aria-label="Go to dashboard"
            >
              <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-white shadow-sm ring-1 ring-black/5">
                <img src="/mipt_logo.png" alt="MAIPT" className="h-8 w-8 object-contain" />
              </div>
              {!collapsed && (
                <div>
                  <h1 className="text-lg font-semibold tracking-[-0.01em] text-gray-900">MAIPT</h1>
                  <p className="text-xs text-gray-500">Practical Training</p>
                </div>
              )}
            </button>

            <button
              onClick={() => setCollapsed((prev) => !prev)}
              className="rounded-xl p-2 text-gray-500 transition-colors hover:bg-gray-100 hover:text-gray-800"
              aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            >
              <ChevronLeft className={`h-5 w-5 transition-transform ${collapsed ? 'rotate-180' : ''}`} />
            </button>
          </div>
        </div>

        <nav aria-label="Primary" className="flex-1 overflow-y-auto overflow-x-hidden px-3 py-4 [scrollbar-width:thin]">
          <div className="space-y-1" key={navKey}>
            {primaryNavItems.map((item) => {
              const Icon = item.icon;
              const isActive = activePrimary === item.key;

              return (
                <button
                  key={`${item.key}-${navKey}`}
                  onClick={() => navigate(item.href)}
                  className={`flex w-full items-center gap-3 rounded-2xl px-3 py-3 text-left text-sm font-medium transition-colors ${
                    collapsed ? 'justify-center' : ''
                  } ${isActive ? 'bg-orange-50 text-orange-700' : 'text-gray-700 hover:bg-gray-100'}`}
                  aria-current={isActive ? 'page' : undefined}
                  title={collapsed ? item.label : undefined}
                >
                  <Icon className="h-5 w-5 shrink-0" />
                  {!collapsed && <span>{item.label}</span>}
                </button>
              );
            })}
          </div>

          <div className="mt-8 border-t border-gray-200 pt-4">
            {!collapsed && <p className="px-3 pb-2 text-xs font-semibold uppercase tracking-wide text-gray-400">More</p>}
            <div className="space-y-1">
              {secondaryLinks.map((item) => {
                const Icon = item.icon;
                const isActive = location.pathname === item.href || location.pathname.startsWith(`${item.href}/`);
                const isExternal = (item as any).external;
                
                if (isExternal) {
                  return (
                    <a
                      key={item.href}
                      href={item.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      className={`flex w-full items-center gap-3 rounded-2xl px-3 py-3 text-left text-sm font-medium transition-colors ${
                        collapsed ? 'justify-center' : ''
                      } text-gray-700 hover:bg-gray-100`}
                      title={collapsed ? item.label : undefined}
                    >
                      <Icon className="h-5 w-5 shrink-0" />
                      {!collapsed && <span>{item.label}</span>}
                    </a>
                  );
                }
                
                return (
                  <button
                    key={item.href}
                    onClick={() => navigate(item.href)}
                    className={`flex w-full items-center gap-3 rounded-2xl px-3 py-3 text-left text-sm font-medium transition-colors ${
                      collapsed ? 'justify-center' : ''
                    } ${isActive ? 'bg-orange-50 text-orange-700' : 'text-gray-700 hover:bg-gray-100'}`}
                    title={collapsed ? item.label : undefined}
                  >
                    <Icon className="h-5 w-5 shrink-0" />
                    {!collapsed && <span>{item.label}</span>}
                  </button>
                );
              })}
            </div>
          </div>
        </nav>

        <div className="shrink-0 border-t border-gray-200 px-3 py-4">
          {!collapsed && (
            <div className="mb-3 flex items-center gap-3 rounded-2xl border border-gray-200 bg-gray-50 px-3 py-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-full bg-orange-500 text-sm font-semibold text-white">
                {user?.first_name?.[0] || ''}{user?.last_name?.[0] || ''}
              </div>
              <div className="min-w-0 flex-1">
                <p className="text-sm font-medium text-gray-900 truncate">{user?.first_name} {user?.last_name}</p>
                <p className="text-xs text-gray-500 truncate">{user?.email}</p>
              </div>
            </div>
          )}

          <div className={`mb-3 flex items-center rounded-2xl border border-orange-100 bg-orange-50 px-3 py-3 text-orange-700 ${collapsed ? 'justify-center' : 'gap-3'}`}>
            <Coins className="h-5 w-5 shrink-0" />
            {!collapsed && (
              <div>
                <p className="text-xs font-medium text-orange-500">Available tokens</p>
                <p className="text-sm font-semibold">
                  {balanceLoading ? 'Loading...' : (userBalance?.available_tokens ?? 0) === 0 ? 'Add Token' : (userBalance?.available_tokens ?? 0)}
                </p>
              </div>
            )}
          </div>

          <button
            onClick={handleLogout}
            className={`flex w-full items-center gap-3 rounded-2xl px-3 py-3 text-left text-sm font-medium text-red-600 transition-colors hover:bg-red-50 ${collapsed ? 'justify-center' : ''}`}
            title={collapsed ? 'Logout' : undefined}
          >
            <LogOut className="h-5 w-5 shrink-0" />
            {!collapsed && <span>Logout</span>}
          </button>
        </div>
      </aside>

      <main className="flex flex-1 flex-col overflow-hidden bg-[#f7f5ef]">
        <header className="flex h-16 shrink-0 items-center justify-end gap-3 border-b border-white/70 bg-[#fffaf2]/92 px-6 backdrop-blur-xl">
          <NotificationBell />
        </header>
        <div className="flex-1 overflow-y-auto [scrollbar-gutter:stable]">
          <div className="mx-auto max-w-7xl px-8 py-8">{children}</div>
        </div>
      </main>
    </div>
  );
};
